/**
 * AllBaise Career Hub — Job marketplace with Internship, Job, Bursary, Scholarship, Qualification
 * Colorful card designs matching Deals Hub style with geo-location filtering
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/App";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";
import GeoFilterBar from "@/components/GeoFilterBar";
import {
  Loader2, Briefcase, MapPin, Clock, Search, Plus, X,
  GraduationCap, BookOpen, Award, Wallet, Building2,
  DollarSign, Users, Star, Globe, Filter,
  Check, ExternalLink, Calendar,
} from "lucide-react";

const API = (() => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8001/api";
  return `https://api.${host}/api`;
})();

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CAREER_TYPES = {
  internship: { name: "Internship", icon: BookOpen, color: "from-emerald-500 to-teal-600" },
  job: { name: "Job", icon: Briefcase, color: "from-blue-500 to-cyan-600" },
  bursary: { name: "Bursary", icon: Wallet, color: "from-violet-500 to-purple-600" },
  scholarship: { name: "Scholarship", icon: Award, color: "from-amber-500 to-orange-600" },
  qualification: { name: "Qualification", icon: GraduationCap, color: "from-rose-500 to-pink-600" },
};

const CATEGORIES = [
  "Technology & IT", "Finance & Accounting", "Healthcare & Medical",
  "Engineering & Construction", "Education & Training", "Government & Public Sector",
  "Legal & Compliance", "Sales & Marketing", "Human Resources",
  "Manufacturing & Logistics", "Agriculture & Environment", "Arts & Media",
  "Science & Research", "Hospitality & Tourism", "Retail & Consumer Goods",
  "Energy & Utilities", "Telecommunications", "Transportation & Aviation",
  "Non-Profit & NGO", "Security & Defence",
];

const SA_PROVINCES = [
  "Eastern Cape", "Free State", "Gauteng", "KwaZulu-Natal",
  "Limpopo", "Mpumalanga", "North West", "Northern Cape", "Western Cape",
];

function CareerCard({ career }) {
  const ct = CAREER_TYPES[career.career_type] || { name: career.career_type, icon: Briefcase, color: "from-cyan-600 to-sky-600" };
  const TypeIcon = ct.icon;

  const getStatusBadge = () => {
    if (!career.application_deadline) return null;
    const deadline = new Date(career.application_deadline);
    const daysLeft = Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return <Badge className="bg-red-600/20 text-red-400 text-[9px]">Closed</Badge>;
    if (daysLeft < 7) return <Badge className="bg-red-600/20 text-red-400 text-[9px]">{daysLeft}d left</Badge>;
    if (daysLeft < 30) return <Badge className="bg-amber-600/20 text-amber-400 text-[9px]">{daysLeft}d left</Badge>;
    return <Badge className="bg-emerald-600/20 text-emerald-400 text-[9px]">{daysLeft}d left</Badge>;
  };

  return (
    <Card className="bg-app-card dark:bg-app-card light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 overflow-hidden hover:border-violet-500/30 transition-all">
      <div className={`bg-gradient-to-r ${ct.color} p-4 relative`}>
        <div className="flex items-center justify-between">
          <Badge className="bg-white/20 text-white text-[9px] px-2 py-0.5 rounded-full flex items-center gap-1">
            <TypeIcon className="w-3 h-3" /> {ct.name}
          </Badge>
          {getStatusBadge()}
        </div>
        <h3 className="text-white font-bold text-sm mt-2 line-clamp-2">{career.title}</h3>
        {career.organization && (
          <p className="text-white/70 text-[10px] flex items-center gap-1 mt-1">
            <Building2 className="w-3 h-3" /> {career.organization}
          </p>
        )}
      </div>

      <div className="p-3 space-y-2">
        <div className="flex items-end gap-2">
          <span className="text-sm font-bold text-app">{career.salary_range}</span>
        </div>

        <p className="text-xs text-zinc-500 line-clamp-2">{career.description}</p>

        {career.requirements && career.requirements.length > 0 && (
          <div className="space-y-1">
            {career.requirements.slice(0, 3).map((r, i) => (
              <p key={i} className="text-[10px] text-zinc-400 flex items-start gap-1">
                <Check className="w-2.5 h-2.5 text-emerald-500 mt-0.5 shrink-0" /> {r}
              </p>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3 text-[10px] text-zinc-500 pt-1 border-t border-white/10 dark:border-white/10 light:border-gray-200 flex-wrap">
          {career.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{career.location}</span>}
          {career.duration && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{career.duration}</span>}
          {career.remote_hybrid && (
            <Badge className={`text-[8px] px-1 py-0 ${career.remote_hybrid === "remote" ? "bg-emerald-600/20 text-emerald-400" : career.remote_hybrid === "hybrid" ? "bg-amber-600/20 text-amber-400" : "bg-cyan-900/30 text-cyan-200"}`}>
              {career.remote_hybrid}
            </Badge>
          )}
          {career.distance_km && <span className="text-[9px] text-violet-400">{career.distance_km}km away</span>}
          <span className="ml-auto text-[9px] text-zinc-600">{career.applicants_count || 0} applicants</span>
        </div>

        {career.benefits && career.benefits.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {career.benefits.slice(0, 3).map((b, i) => (
              <Badge key={i} className="bg-violet-600/10 text-violet-300 border-violet-600/20 text-[8px]">{b}</Badge>
            ))}
          </div>
        )}

        <Button className="w-full h-8 text-xs bg-violet-600 hover:bg-violet-500 text-white">
          Apply Now
        </Button>
      </div>
    </Card>
  );
}

export default function CareerHub() {
  const auth = useAuth();
  const user = auth.user;
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [careerType, setCareerType] = useState("all");
  const [category, setCategory] = useState("all");
  const [country, setCountry] = useState("all");
  const [province, setProvince] = useState("all");
  const [radiusKm, setRadiusKm] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", career_type: "job", category: "Technology & IT", organization: "", location: "", province: "", city: "", salary_range: "", application_deadline: "", requirements: "", benefits: "", duration: "", remote_hybrid: "onsite" });
  const [creating, setCreating] = useState(false);

  const fetchCareers = () => {
    setLoading(true);
    axios.get(`${API}/career/list`, { headers: getHeaders() })
      .then(res => setCareers(res.data.items || []))
      .catch(() => setCareers([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCareers(); }, []);

  const filtered = careers.filter(c => {
    if (search && !c.title.toLowerCase().includes(search.toLowerCase()) && !c.description.toLowerCase().includes(search.toLowerCase()) && !(c.organization || "").toLowerCase().includes(search.toLowerCase())) return false;
    if (category !== "all" && c.category !== category) return false;
    if (careerType !== "all" && c.career_type !== careerType) return false;
    if (country !== "all" && c.country !== country) return false;
    if (province !== "all" && c.province !== province) return false;
    if (radiusKm && c.distance_km && parseFloat(c.distance_km) > parseFloat(radiusKm)) return false;
    return c.status === "active";
  });

  const handleCreate = async () => {
    if (!form.title || !form.description) { toast.error("Title and description are required"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/career/create`, {
        ...form,
        requirements: form.requirements ? form.requirements.split(",").map(r => r.trim()).filter(Boolean) : [],
        benefits: form.benefits ? form.benefits.split(",").map(b => b.trim()).filter(Boolean) : [],
      }, { headers: getHeaders() });
      toast.success("Career posted!");
      setShowCreate(false);
      setForm({ title: "", description: "", career_type: "job", category: "Technology & IT", organization: "", location: "", province: "", city: "", salary_range: "", application_deadline: "", requirements: "", benefits: "", duration: "", remote_hybrid: "onsite" });
      fetchCareers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
    setCreating(false);
  };

  return (
    <div className="min-h-screen bg-app text-app">
      <TopNav />
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-violet-400" /> Career Hub
            </h1>
            <p className="text-xs text-zinc-500">Jobs, internships, bursaries & scholarships</p>
          </div>
          <Button onClick={() => setShowCreate(!showCreate)} className="bg-violet-600 hover:bg-violet-500 text-white text-xs h-8">
            {showCreate ? <X className="w-3.5 h-3.5 mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
            {showCreate ? "Cancel" : "Post a Career"}
          </Button>
        </div>

        {showCreate && (
          <Card className="bg-app-card dark:bg-app-card light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 p-4 mb-4">
            <h2 className="text-sm font-semibold mb-3">New Career Posting</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="space-y-1"><label className="text-[10px] text-zinc-500">Title *</label>
                <input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                  className="w-full bg-cyan-950/40 border border-cyan-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-violet-500" /></div>
              <div className="space-y-1"><label className="text-[10px] text-zinc-500">Type</label>
                <select value={form.career_type} onChange={e => setForm({...form, career_type: e.target.value})}
                  className="w-full bg-cyan-950/40 border border-cyan-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-violet-500">
                  {Object.entries(CAREER_TYPES).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}</select></div>
              <div className="space-y-1"><label className="text-[10px] text-zinc-500">Category</label>
                <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                  className="w-full bg-cyan-950/40 border border-cyan-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-violet-500">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
              <div className="space-y-1"><label className="text-[10px] text-zinc-500">Organization</label>
                <input value={form.organization} onChange={e => setForm({...form, organization: e.target.value})}
                  className="w-full bg-cyan-950/40 border border-cyan-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-violet-500" /></div>
              <div className="space-y-1"><label className="text-[10px] text-zinc-500">Province</label>
                <select value={form.province} onChange={e => setForm({...form, province: e.target.value})}
                  className="w-full bg-cyan-950/40 border border-cyan-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-violet-500">
                  <option value="">Select...</option>{SA_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}</select></div>
              <div className="space-y-1"><label className="text-[10px] text-zinc-500">City</label>
                <input value={form.city} onChange={e => setForm({...form, city: e.target.value})}
                  className="w-full bg-cyan-950/40 border border-cyan-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-violet-500" /></div>
              <div className="space-y-1"><label className="text-[10px] text-zinc-500">Salary Range</label>
                <input value={form.salary_range} onChange={e => setForm({...form, salary_range: e.target.value})}
                  className="w-full bg-cyan-950/40 border border-cyan-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-violet-500" /></div>
              <div className="space-y-1"><label className="text-[10px] text-zinc-500">Deadline</label>
                <input type="date" value={form.application_deadline} onChange={e => setForm({...form, application_deadline: e.target.value})}
                  className="w-full bg-cyan-950/40 border border-cyan-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-violet-500" /></div>
              <div className="space-y-1"><label className="text-[10px] text-zinc-500">Duration</label>
                <input value={form.duration} onChange={e => setForm({...form, duration: e.target.value})}
                  className="w-full bg-cyan-950/40 border border-cyan-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-violet-500" /></div>
              <div className="space-y-1"><label className="text-[10px] text-zinc-500">Remote/Hybrid/Onsite</label>
                <select value={form.remote_hybrid} onChange={e => setForm({...form, remote_hybrid: e.target.value})}
                  className="w-full bg-cyan-950/40 border border-cyan-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-violet-500">
                  <option value="onsite">On-Site</option><option value="hybrid">Hybrid</option><option value="remote">Remote</option></select></div>
            </div>
            <div className="space-y-1 mt-3"><label className="text-[10px] text-zinc-500">Description *</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2}
                className="w-full bg-cyan-950/40 border border-cyan-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-violet-500" /></div>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div className="space-y-1"><label className="text-[10px] text-zinc-500">Requirements (comma separated)</label>
                <input value={form.requirements} onChange={e => setForm({...form, requirements: e.target.value})}
                  className="w-full bg-cyan-950/40 border border-cyan-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-violet-500" /></div>
              <div className="space-y-1"><label className="text-[10px] text-zinc-500">Benefits (comma separated)</label>
                <input value={form.benefits} onChange={e => setForm({...form, benefits: e.target.value})}
                  className="w-full bg-cyan-950/40 border border-cyan-800/40 rounded-lg px-3 py-1.5 text-xs text-app focus:outline-none focus:border-violet-500" /></div>
            </div>
            <Button onClick={handleCreate} disabled={creating} className="mt-3 bg-violet-600 hover:bg-violet-500 text-white text-xs h-8">
              {creating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
              Create Listing
            </Button>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search careers..."
              className="w-full bg-cyan-950/40 border border-cyan-800/40 rounded-lg pl-8 pr-3 py-1.5 text-xs text-app placeholder:text-zinc-500 focus:outline-none focus:border-violet-500" />
          </div>
          <div className="flex gap-1 flex-wrap">
            <button onClick={() => setCareerType("all")}
              className={`text-[10px] px-2.5 py-1 rounded-full ${careerType === "all" ? "bg-violet-600/30 text-violet-300" : "bg-zinc-800/50 text-zinc-500"}`}>All</button>
            {Object.entries(CAREER_TYPES).map(([k, v]) => (
              <button key={k} onClick={() => setCareerType(k)}
                className={`text-[10px] px-2.5 py-1 rounded-full ${careerType === k ? "bg-violet-600/30 text-violet-300" : "bg-zinc-800/50 text-zinc-500"}`}>{v.name}</button>
            ))}
          </div>
        </div>

        {/* Category + Province + Geo pills */}
        <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1 flex-wrap">
          <button onClick={() => setCategory("all")}
            className={`text-[10px] px-3 py-1 rounded-full whitespace-nowrap ${category === "all" ? "bg-violet-600/30 text-violet-300" : "bg-zinc-800/50 text-zinc-500"}`}>All Categories</button>
          {CATEGORIES.slice(0, 10).map(c => (
            <button key={c} onClick={() => setCategory(c)}
              className={`text-[10px] px-3 py-1 rounded-full whitespace-nowrap ${category === c ? "bg-violet-600/30 text-violet-300" : "bg-zinc-800/50 text-zinc-500"}`}>{c}</button>
          ))}
        </div>

        <GeoFilterBar country={country} onCountryChange={setCountry} region={province} onRegionChange={setProvince} radius={radiusKm} onRadiusChange={setRadiusKm} />

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
        ) : filtered.length === 0 ? (
          <Card className="bg-app-card dark:bg-app-card light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 p-8 text-center">
            <Briefcase className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">No career listings found</p>
            <Button onClick={() => setShowCreate(true)} variant="outline" size="sm" className="mt-3 text-xs border-violet-600 text-violet-500">
              <Plus className="w-3 h-3 mr-1" /> Post the first listing
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(career => <CareerCard key={career.id} career={career} />)}
          </div>
        )}
      </div>
    </div>
  );
}
